import styled from 'styled-components';
import { SessionSidebar } from './SessionSidebar';
import type { OpencodeStatus, Session } from '../types';

export type AppTab = 'overview' | 'ai';

const Wrap = styled.div`
  min-height: 100%;
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const Brand = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 6px 8px;
`;

const LogoMark = styled.div`
  width: 38px;
  height: 38px;
  display: grid;
  place-items: center;
  border: 1px solid ${p => p.theme.primary};
  border-radius: 13px;
  background: ${p => p.theme.primarySoft};
  color: ${p => p.theme.primary};
  font-weight: 900;
  box-shadow: 0 10px 24px ${p => p.theme.primarySoft};
`;

const BrandText = styled.div`
  min-width: 0;
`;

const Title = styled.div`
  color: ${p => p.theme.text};
  font-family: 'Raleway', system-ui, sans-serif;
  font-size: 20px;
  font-weight: 900;
  letter-spacing: -0.04em;
`;

const Subtitle = styled.div`
  margin-top: 2px;
  color: ${p => p.theme.muted};
  font-size: 12px;
  font-weight: 700;
`;

const Nav = styled.nav`
  display: grid;
  gap: 8px;
`;

const NavButton = styled.button<{ $active?: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 13px 14px;
  border: 1px solid ${p => (p.$active ? p.theme.primarySoft : 'transparent')};
  border-radius: 13px;
  background: ${p => (p.$active ? p.theme.primarySoft : 'transparent')};
  color: ${p => (p.$active ? p.theme.primary : p.theme.text)};
  font-size: 14px;
  font-weight: 900;
  text-align: left;
  transition: background .15s ease, color .15s ease, transform .15s ease;

  &::before {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 999px;
    background: ${p => (p.$active ? p.theme.primary : p.theme.border)};
    box-shadow: ${p => (p.$active ? `0 0 0 4px ${p.theme.primarySoft}` : 'none')};
  }

  &:hover {
    background: ${p => p.theme.primarySoft};
    color: ${p => p.theme.primary};
    transform: translateX(2px);
  }
`;

const SessionArea = styled.div`
  min-height: 0;
  padding-top: 4px;
`;

const OverviewNote = styled.div`
  margin-top: 2px;
  padding: 16px;
  border: 1px solid ${p => p.theme.border};
  border-radius: 16px;
  background: ${p => p.theme.surfaceSoft};
  color: ${p => p.theme.muted};
  font-size: 13px;
  line-height: 1.45;
`;

const Footer = styled.div`
  margin-top: auto;
  padding: 12px 8px 0;
  color: ${p => p.theme.muted};
  font-size: 12px;
  font-weight: 700;
`;

type Props = {
  activeTab: AppTab;
  sessions: Session[];
  selectedSession: Session | null;
  status: OpencodeStatus;
  onTabChange: (tab: AppTab) => void;
  onCreateSession: (name: string) => void;
  onDeleteSession: (id: string) => void;
  onSelectSession: (session: Session) => void;
};

export function NavigationSidebar({ activeTab, sessions, selectedSession, status, onTabChange, onCreateSession, onDeleteSession, onSelectSession }: Props) {
  return (
    <Wrap>
      <Brand>
        <LogoMark>B</LogoMark>
        <BrandText>
          <Title>BlanketChat</Title>
          <Subtitle>Support console</Subtitle>
        </BrandText>
      </Brand>

      <Nav aria-label="Main navigation">
        <NavButton type="button" $active={activeTab === 'overview'} onClick={() => onTabChange('overview')}>Overview</NavButton>
        <NavButton type="button" $active={activeTab === 'ai'} onClick={() => onTabChange('ai')}>AI Questions</NavButton>
      </Nav>

      {activeTab === 'ai' ? (
        <SessionArea>
          <SessionSidebar
            sessions={sessions}
            selectedSession={selectedSession}
            status={status}
            onCreateSession={onCreateSession}
            onDeleteSession={onDeleteSession}
            onSelectSession={onSelectSession}
          />
        </SessionArea>
      ) : (
        <OverviewNote>
          Overview follows the selected context: everything, company, workspace, user, or combinations.
        </OverviewNote>
      )}

      <Footer>Context-aware support workspace</Footer>
    </Wrap>
  );
}
